import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectAllBuyManagers } from '../../../../../store/slices/buyManager/buyManagers';

const STORAGE_KEY = 'swarmsHistory_rows';

const completeSwarmInfo = (metrics, buyManagers = [], additionalSwarms = [], additionalEvents = [], storeSwarms = [], storeEvents = []) => {
  if (!metrics) return [];
  
  return metrics.map(metric => {
    // First try to find the swarm in the store
    let swarmFound = storeSwarms?.find(s => s.id === metric.swarmId);
    if (!swarmFound) {
      // If not in store, look in additional swarms
      swarmFound = additionalSwarms?.find(s => s.id === metric.swarmId);
    }
    
    // First try to find the event in the store
    let eventFound = storeEvents?.find(e => e.bteId === swarmFound?.bteId);
    if (!eventFound) {
      // If not in store, look in additional events
      eventFound = additionalEvents?.find(e => e.bteId === swarmFound?.bteId);
    }

    // Find buy manager either from metric or using swarm's buyManagerId
    let buyManager = metric.buyManager || buyManagers?.find(BM => BM.buyManagerID === swarmFound?.buyManagerId);

    return {
      ...metric,
      swarm: swarmFound,
      event: eventFound,
      buyManager,
      id: metric.id,
      salesDate: metric.salesDate || swarmFound?.salesDate || null,
      venueName: metric.venueName || eventFound?.venue?.name || null
    };
  });
};

export const useSwarmRows = (apiResponse, getSwarms, getBuyManagers) => {
  const [rows, setRows] = useState(() => {
    const storedRows = sessionStorage.getItem(STORAGE_KEY);
    return storedRows ? JSON.parse(storedRows) : [];
  });
  
  const requestedSwarmIds = useRef(new Set());
  const swarms = useSelector(state => state.swarmsSlice.swarms);
  const events = useSelector(state => state.swarmsSlice.events);
  const buyManagers = useSelector(selectAllBuyManagers);

  const setRowsWithStorage = (newRows) => {
    setRows(newRows);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newRows));
  };

  useEffect(() => {
    const updateRows = async () => {
      if (!apiResponse?.swarmMetrics?.length) {
        setRowsWithStorage([]);
        return;
      }

      const missingSwarmIds = apiResponse.swarmMetrics
        .filter(metric => {
          const isInStore = swarms?.some(s => s.id === metric.swarmId);
          const wasRequested = requestedSwarmIds.current.has(metric.swarmId);
          return !isInStore && !wasRequested;
        })
        .map(metric => metric.swarmId);

      let completedRows = [];

      // First, get any missing swarms and complete initial row data
      if (missingSwarmIds.length > 0) {
        missingSwarmIds.forEach(id => requestedSwarmIds.current.add(id));
        
        const query = `?${missingSwarmIds.map(id => `id=${id}`).join('&')}`;
        try {
          const response = await getSwarms(query).unwrap();
          completedRows = completeSwarmInfo(apiResponse.swarmMetrics, buyManagers, response.swarms, response.events, swarms, events);
        } catch (error) {
          console.error('Error fetching missing swarms:', error);
          completedRows = completeSwarmInfo(apiResponse.swarmMetrics, buyManagers, [], [], swarms, events);
        }
      } else {
        completedRows = completeSwarmInfo(apiResponse.swarmMetrics, buyManagers, [], [], swarms, events);
      }

      // Then, check for any missing buy managers
      const missingBuyManagerIds = Array.from(new Set(
        completedRows
          .filter(row => {
            const swarmBuyManagerId = row?.swarm?.buyManagerId;
            return swarmBuyManagerId && !buyManagers?.some(bm => bm.buyManagerID === swarmBuyManagerId);
          })
          .map(row => row.swarm.buyManagerId)
      ));
      
      // If we have missing buy managers, fetch them and update the rows one final time
      if (missingBuyManagerIds.length > 0) {
        try {
          const response = await getBuyManagers({ id: missingBuyManagerIds }).unwrap();
          completedRows = completeSwarmInfo(completedRows, response, [], [], swarms, events);
        } catch (error) {
          console.error('Error fetching buy managers:', error);
        }
      }

      setRowsWithStorage(completedRows);
    };

    updateRows();
  }, [apiResponse, swarms, events, buyManagers, getSwarms, getBuyManagers]);

  useEffect(() => {
    if (apiResponse) {
      requestedSwarmIds.current.clear();
    }
  }, [apiResponse]);

  return { rows };
};
